import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchOptimizations } from "../slice/optimizationSlice";
import { fetchCandidates } from "../slice/candidateSlice";
import { fetchClients } from "../slice/clientSlice";
import { useDialog } from "../components/ConfirmDialog";
import { viewFileInNewTab, downloadFile } from "../utils/viewFile";
import { EyeIcon, DownloadIcon } from "../components/icons";
import OptimizationFilters from "../components/OptimizationFilters";
import Pagination from "../components/Pagination";
// Shares page, table and button styles with the Clients/Candidates pages;
// OptimizationList.css adds the status badge, score and resume cells.
import "../css/ClientList.css";
import "../css/OptimizationList.css";

// "NEEDS_REVIEW" -> "Needs review"
const statusLabel = (s = "") => {
  const text = s.toLowerCase().replace(/_/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
};

function StatusBadge({ status }) {
  const key = (status || "DRAFT").toLowerCase().replace(/_/g, "-");
  return (
    <span className={`op-status op-status-${key}`}>
      <span className="op-status-dot" aria-hidden="true" />
      {statusLabel(status || "DRAFT")}
    </span>
  );
}

// Initial -> final ATS score, with the change between them.
function AtsScore({ ats }) {
  const initial = ats?.initialScore;
  const final = ats?.finalScore;

  if (initial == null && final == null) return <span className="cl-dash">—</span>;

  if (final == null) {
    return (
      <div className="op-ats">
        <span className="op-ats-line">
          <strong>{initial}</strong>
        </span>
        <span className="op-ats-note">Initial score</span>
      </div>
    );
  }

  const delta = initial != null ? final - initial : null;
  const deltaClass = delta == null ? "" : delta > 0 ? "is-up" : delta < 0 ? "is-down" : "is-flat";

  return (
    <div className="op-ats">
      <span className="op-ats-line">
        {initial != null && (
          <>
            <span className="op-ats-initial">{initial}</span>
            <span className="op-ats-arrow" aria-label="to">
              →
            </span>
          </>
        )}
        <strong>{final}</strong>
      </span>
      {delta != null && (
        <span className={`op-ats-delta ${deltaClass}`}>
          {delta > 0 ? `+${delta}` : delta === 0 ? "No change" : delta}
        </span>
      )}
    </div>
  );
}

const initials = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";

const EMPTY_FILTERS = {
  candidateId: "",
  clientId: "",
  status: "",
  jdTitle: "",
  startDate: "",
  endDate: "",
  minAtsScore: "",
};
const PAGE_SIZE = 10;

export default function OptimizationList() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { optimizations, pagination, status, error } = useSelector((state) => state.optimizations);
  const { candidates } = useSelector((state) => state.candidates);
  const { clients } = useSelector((state) => state.clients);
  const loading = status === "loading";
  const [viewingId, setViewingId] = useState(null);
  const { dialogElement, notify } = useDialog();
  const isAdmin = user?.role === "ADMIN";

  const [draftFilters, setDraftFilters] = useState(EMPTY_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(1);

  useEffect(() => {
    dispatch(fetchOptimizations({ ...appliedFilters, page, limit: PAGE_SIZE }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, page]);

  // Unpaginated on purpose — these two just populate the filter
  // dropdowns, they need the full candidate/client lists.
  useEffect(() => {
    dispatch(fetchCandidates());
    dispatch(fetchClients());
  }, [dispatch]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters(draftFilters);
    dispatch(fetchOptimizations({ ...draftFilters, page: 1, limit: PAGE_SIZE }));
  };

  const resetFilters = () => {
    setDraftFilters(EMPTY_FILTERS);
    setAppliedFilters(EMPTY_FILTERS);
    setPage(1);
    dispatch(fetchOptimizations({ page: 1, limit: PAGE_SIZE }));
  };

  // resumeToView (added by the backend) points at the FINAL resume once
  // one's been generated for this optimization, otherwise the master
  // resume it started from.
  const handleViewResume = async (optimization) => {
    const { resumeToView, candidateId } = optimization;
    if (!resumeToView || !candidateId?._id) return;

    setViewingId(optimization._id);
    await viewFileInNewTab(
      `/candidate/${candidateId._id}/resume-versions/${resumeToView.versionId}/download`,
      { onError: () => notify("Could not open this resume. Please try again.", { title: "View resume failed" }) }
    );
    setViewingId(null);
  };

  const handleDownloadResume = async (optimization) => {
    const { resumeToView, candidateId } = optimization;
    if (!resumeToView || !candidateId?._id) return;

    await downloadFile(
      `/candidate/${candidateId._id}/resume-versions/${resumeToView.versionId}/download`,
      resumeToView.fileName,
      { onError: () => notify("Could not download this resume. Please try again.", { title: "Download failed" }) }
    );
  };

  const renderTable = () => {
    if (loading && optimizations.length === 0) {
      return <div className="cl-empty">Loading optimizations…</div>;
    }
    if (optimizations.length === 0) {
      return (
        <div className="cl-empty">
          <strong>No optimizations match these filters.</strong>
          <span>{isAdmin ? "Try clearing the filters above." : "Clear the filters, or start a new optimization."}</span>
        </div>
      );
    }

    return (
      <>
        <div className="cl-table-wrap">
          <table className="cl-table op-table">
            <colgroup>
              <col className="op-col-candidate" />
              <col className="op-col-role" />
              <col className="op-col-status" />
              <col className="op-col-ats" />
              <col className="op-col-resume" />
              <col className="op-col-date" />
              <col className="op-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th>Candidate</th>
                <th>Role &amp; client</th>
                <th>Status</th>
                <th>ATS score</th>
                <th>Resume</th>
                <th>Created</th>
                <th className="cl-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {optimizations.map((o) => {
                const role = o.jobDescription?.title || o.jobDescription?.fileName;
                const resume = o.resumeToView;
                const isFinal = resume?.type === "FINAL";
                return (
                  <tr key={o._id}>
                    <td>
                      <div className="cl-name">
                        <span className="cl-mono">{initials(o.candidateId?.name)}</span>
                        <span className="cl-name-text op-ellipsis" title={o.candidateId?.name}>
                          {o.candidateId?.name || "—"}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="op-role">
                        <Link to={`/dashboard/optimizations/${o._id}`} className="op-role-title" title={role || ""}>
                          {role || <span className="op-untitled">Untitled role</span>}
                        </Link>
                        <span className="op-role-client">{o.clientId?.name || "No client"}</span>
                      </div>
                    </td>

                    <td>
                      <StatusBadge status={o.status} />
                    </td>

                    <td>
                      <AtsScore ats={o.ats} />
                    </td>

                    <td>
                      {resume ? (
                        <div className="op-resume">
                          <span
                            className={`op-resume-type${isFinal ? " is-final" : ""}`}
                            title={isFinal ? "Final generated resume" : "Master resume this optimization started from"}
                          >
                            {isFinal ? "Final" : "Master"}
                          </span>
                          <button
                            type="button"
                            className="cl-btn cl-btn-row op-icon-btn"
                            onClick={() => handleViewResume(o)}
                            disabled={viewingId === o._id}
                            title={isFinal ? "Open the final generated resume" : "Open the master resume"}
                            aria-label="View resume"
                          >
                            <EyeIcon />
                          </button>
                          <button
                            type="button"
                            className="cl-btn cl-btn-row op-icon-btn"
                            onClick={() => handleDownloadResume(o)}
                            title={`Download ${resume.fileName || "resume"}`}
                            aria-label="Download resume"
                          >
                            <DownloadIcon />
                          </button>
                        </div>
                      ) : (
                        <span className="cl-dash">—</span>
                      )}
                    </td>

                    <td className="op-date">{new Date(o.createdAt).toLocaleDateString()}</td>

                    <td className="cl-actions">
                      <Link to={`/dashboard/optimizations/${o._id}`} className="cl-btn cl-btn-row">
                        Open
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="cl-pagination">
          <Pagination pagination={pagination} onPageChange={setPage} />
        </div>
      </>
    );
  };

  return (
    <div className="cl-page">
      <header className="cl-header">
        <div>
          <h2>Optimizations</h2>
          <p>Resumes tailored to specific job descriptions, with their ATS scores.</p>
        </div>
        {!isAdmin && (
          <Link to="/dashboard/optimizations/new" className="cl-btn cl-btn-add">
            <span aria-hidden="true">+</span> Start optimization
          </Link>
        )}
      </header>

      {error && (
        <div className="cl-error" role="alert">
          {error}
        </div>
      )}

      <div className="cl-filters">
        <OptimizationFilters
          value={draftFilters}
          onChange={setDraftFilters}
          onApply={applyFilters}
          onReset={resetFilters}
          candidates={candidates}
          clients={clients}
        />
      </div>

      <section className="cl-card">
        <div className="cl-card-head">
          <h2>{isAdmin ? "All optimizations" : "Your optimizations"}</h2>
        </div>
        {renderTable()}
      </section>

      {dialogElement}
    </div>
  );
}
